/**
 * Input.tsx - Core Form Input Component with forwardRef
 * FIXED: Now properly handles refs using React.forwardRef()
 */

import React from 'react';
import { clsx } from 'clsx';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    error?: string;
    helperText?: string;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    variant?: 'default' | 'filled';
    inputSize?: 'sm' | 'md' | 'lg';
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(({
                                                                         label,
                                                                         error,
                                                                         helperText,
                                                                         leftIcon,
                                                                         rightIcon,
                                                                         variant = 'default',
                                                                         inputSize = 'md',
                                                                         className,
                                                                         id,
                                                                         required,
                                                                         disabled,
                                                                         ...props
                                                                     }, ref) => {
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

    const sizeClasses = {
        sm: 'px-3 py-2 text-sm',
        md: 'px-3 py-2 text-sm',
        lg: 'px-4 py-3 text-base'
    };

    const variantClasses = {
        default: 'bg-white border-gray-300',
        filled: 'bg-gray-50 border-gray-200'
    };

    return (
        <div className={clsx('w-full', className)}>
            {label && (
                <label
                    htmlFor={inputId}
                    className="block text-sm font-medium text-gray-700 mb-1"
                >
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </label>
            )}

            <div className="relative">
                {leftIcon && (
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        {leftIcon}
                    </div>
                )}

                <input
                    ref={ref} // Forward the ref to the actual input element
                    id={inputId}
                    className={clsx(
                        'block w-full border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors',
                        sizeClasses[inputSize],
                        variantClasses[variant],
                        leftIcon && 'pl-10',
                        rightIcon && 'pr-10',
                        error && 'border-red-300 focus:ring-red-500 focus:border-red-500',
                        disabled && 'bg-gray-50 text-gray-500 cursor-not-allowed'
                    )}
                    disabled={disabled}
                    {...props}
                />

                {rightIcon && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        {rightIcon}
                    </div>
                )}
            </div>

            {error && (
                <p className="mt-1 text-sm text-red-600">{error}</p>
            )}

            {helperText && !error && (
                <p className="mt-1 text-sm text-gray-500">{helperText}</p>
            )}
        </div>
    );
});

// Add display name for better debugging
Input.displayName = 'Input';